import React from "react";
import PropTypes from "prop-types";
import { Table, Badge } from "react-bootstrap";
import SimpleBar from "simplebar-react";
import "simplebar-react/dist/simplebar.min.css";
import { useSelector } from "react-redux";

// Utility functions for safe number handling
const safeNumber = (value, defaultValue = 0) => {
  const num = Number(value);
  return isNaN(num) ? defaultValue : num;
};

const safePercentage = (numerator, denominator, decimals = 1) => {
  if (!denominator || denominator === 0) return "0%";
  const percentage = (safeNumber(numerator) / safeNumber(denominator)) * 100;
  return `${percentage.toFixed(decimals)}%`;
};

// Status label mapping
const getStatusLabel = (key) => {
  const labelMap = {
    approved: "Approved",
    rejected: "Rejected",
    partial: "Partially Approved",
    pended: "Pended",
    noresp: "No Response",
    error: "Error",
    valid: "Valid",
    invalid: "Invalid",
    "validation-error": "Validation Error",
    cancel: "Cancelled",
    queue: "Queued",
    adjudication: "Adjudication",
    "not-required": "Not Required",
    prepared: "Prepared",
    withdrawn: "Withdrawn",
  };
  return labelMap[key] || key.charAt(0).toUpperCase() + key.slice(1);
};

// Status config factory
const getStatusConfig = (status) => {
  const normalized = status.toLowerCase().trim();

  const baseColors = {
    approved: { dotBg: "#2c7ce6", badgeBg: "success" },
    pended: { dotBg: "#d16e34", badgeBg: "warning" },
    queued: { dotBg: "#2a91ed" },
    rejected: { dotBg: "#ed5e0b", badgeLabel: "High Alert", badgeBg: "danger" },
    partial: { dotBg: "#0da9bd" },
    cancelled: { dotBg: "#269dd0" },
    cancel: { dotBg: "#269dd0" },
    invalid: { dotBg: "#ed5e0b", badgeBg: "danger" },
    "no response": { dotBg: "#26bcfc" },
    noresp: { dotBg: "#26bcfc" },
    withdrawn: { dotBg: "#d63384" },
    error: { dotBg: "#f5803e", badgeBg: "warning" },
    ready_to_submit: { dotBg: "#2c7ce6" },
    prepared: { dotBg: "#a9e2fd" },
    adjudication: { dotBg: "#b8571f" },
    "not-required": { dotBg: "#197aa4" },
    queue: { dotBg: "#2a91ed" },
    "validation-error": { dotBg: "#ed5e0b", badgeBg: "danger" },
  };

  const colorConfig = baseColors[normalized] || {
    dotBg: "#999999",
    badgeLabel: null,
    badgeBg: null,
  };

  return {
    ...colorConfig,
  };
};

// Row component
const StatusBreakdownTableRow = ({ item, isLast }) => {
  const config = getStatusConfig(item.status);

  return (
    <tr>
      <td
        className={`align-middle font-sans-serif fw-medium text-nowrap ${
          isLast ? "border-bottom-0" : ""
        }`}
      >
        <span
          style={{
            height: "12px",
            width: "12px",
            backgroundColor: config.dotBg,
            borderRadius: "50%",
            display: "inline-block",
            marginRight: "8px",
          }}
        ></span>
        {item.status === "Ready_to_submit" ? "Ready to Submit" : item.status}
      </td>
      <td
        className={`align-middle text-center ${
          isLast ? "border-bottom-0" : ""
        }`}
      >
        {item.count.toLocaleString()}
      </td>
      <td
        className={`align-middle text-center ${
          isLast ? "border-bottom-0" : ""
        }`}
      >
        {item.value.toLocaleString()}
      </td>
      <td
        className={`align-middle text-center ${
          isLast ? "border-bottom-0" : ""
        }`}
      >
        {item.percentage}
      </td>
    </tr>
  );
};

StatusBreakdownTableRow.propTypes = {
  item: PropTypes.shape({
    status: PropTypes.string.isRequired,
    count: PropTypes.number.isRequired,
    value: PropTypes.number.isRequired,
    percentage: PropTypes.string.isRequired,
  }),
  isLast: PropTypes.bool.isRequired,
};

// Table
const StatusBreakdownTable = ({ data }) => {
  return (
    <>
      <SimpleBar>
        <h6 className="fs-9 mt-3 text-capitalize fw-normal text-primary">
          claim status summary{" "}
        </h6>
        <Table className="fs-10  mb-0" bordered hover responsive>
          <thead className="bg-200">
            <tr>
              <th className="text-800 text-nowrap">Status</th>
              <th className="text-800 text-nowrap text-center">Count</th>
              <th className="text-800 text-nowrap text-center">Value (SAR)</th>
              <th className="text-800 text-nowrap text-center">Percentage</th>
            </tr>
          </thead>
          <tbody>
            {data.map((item, index) => (
              <StatusBreakdownTableRow
                key={item.status}
                item={item}
                isLast={index === data.length - 1}
              />
            ))}
          </tbody>
        </Table>
      </SimpleBar>
    </>
  );
};

StatusBreakdownTable.propTypes = {
  data: PropTypes.arrayOf(StatusBreakdownTableRow.propTypes.item).isRequired,
};

// Main component
export default function StatusBreakdownState() {
  const { claimDashboardData } = useSelector((state) => state.claimDashboard);
  const claimData = claimDashboardData?.claim || {};

  // Safely get total claims
  const totalClaims = safeNumber(claimData.total, 0);
  const totalSubmittedAmount = safeNumber(
    claimDashboardData?.totalSumitedAmount,
    0
  );

  // Dynamically create status data from the actual data structure
  const createStatusData = (data) => {
    const statusKeys = Object.keys(data).filter(
      (key) =>
        key !== "total" && typeof data[key] === "object" && data[key] !== null
    );

    return statusKeys.map((key) => ({
      key,
      label: getStatusLabel(key),
      data: data[key] || {},
    }));
  };

  const statusData = createStatusData(claimData);

  // Create table data with safe number handling
  const tableData = statusData
    .map(({ label, data }) => {
      const count = safeNumber(data?.total, 0);
      const value = safeNumber(data?.tot_submtamount, 0);
      const percentage = safePercentage(count, totalClaims);

      return {
        status: label,
        count,
        value,
        percentage,
      };
    })

    // Sort by count in descending order
    .sort((a, b) => b.count - a.count);

  // If no data, show a message
  if (tableData.length === 0) {
    return (
      <div className="text-center py-4">
        <p className="text-muted mb-0">No claim data available</p>
      </div>
    );
  }

  return <StatusBreakdownTable data={tableData} />;
}
